import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import { Wallet as WalletIcon, ArrowLeft, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const PROVIDERS = [
    { id: "orange", name: "Orange Money", color: "#FF6600" },
    { id: "mtn", name: "MTN MoMo", color: "#FFCC00" },
    { id: "moov", name: "Moov Money", color: "#005BAC" },
    { id: "wave", name: "Wave", color: "#1DCFFF" },
];

export default function Wallet() {
    const { user, loading, checkAuth } = useAuth();
    const navigate = useNavigate();
    const [packs, setPacks] = useState({});
    const [txns, setTxns] = useState([]);
    const [selectedPack, setSelectedPack] = useState(null);
    const [provider, setProvider] = useState("orange");
    const [phone, setPhone] = useState("");
    const [paying, setPaying] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    useEffect(() => {
        if (!loading && !user) navigate("/login");
    }, [user, loading, navigate]);

    useEffect(() => {
        api.get("/credits/packs").then((r) => setPacks(r.data)).catch(() => {});
        if (user) {
            api.get("/credits/transactions").then((r) => setTxns(r.data)).catch(() => {});
        }
    }, [user]);

    const openBuy = (packId) => {
        setSelectedPack(packId);
        setPhone(user?.whatsapp_number || "");
        setDialogOpen(true);
    };

    const confirmPay = async () => {
        if (!phone || phone.length < 6) {
            toast.error("Entrez un numéro de téléphone valide");
            return;
        }
        setPaying(true);
        try {
            const { data } = await api.post("/credits/purchase", {
                provider,
                phone_number: phone,
                pack_id: selectedPack,
            });
            toast.success(`+${data.pack.credits} crédits ajoutés !`);
            setDialogOpen(false);
            await checkAuth();
            const t = await api.get("/credits/transactions");
            setTxns(t.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Paiement échoué");
        } finally {
            setPaying(false);
        }
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-[#050505] text-white pb-32 grain-bg">
            <div className="max-w-lg mx-auto px-4 pt-6">
                <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white mb-6" data-testid="back-btn">
                    <ArrowLeft className="w-5 h-5" /> <span className="font-body text-sm">Retour</span>
                </button>

                {/* Balance */}
                <div className="glass rounded-3xl p-6 mb-6 relative overflow-hidden">
                    <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#FFC107]/20 blur-3xl" />
                    <div className="relative">
                        <div className="flex items-center gap-2 mb-2">
                            <WalletIcon className="w-5 h-5 text-[#FFC107]" />
                            <p className="text-xs uppercase tracking-widest text-white/60 font-body">Votre solde</p>
                        </div>
                        <p className="font-display font-black text-6xl" data-testid="wallet-balance">
                            {user.credits ?? 0} <span className="text-2xl text-white/60">crédits</span>
                        </p>
                    </div>
                </div>

                <h2 className="font-display font-black text-2xl mb-3">Recharger</h2>
                <div className="space-y-3 mb-8">
                    {Object.entries(packs).map(([id, p]) => (
                        <button
                            key={id}
                            data-testid={`pack-${id}`}
                            onClick={() => openBuy(id)}
                            className="w-full glass rounded-2xl p-4 flex items-center justify-between hover:border-[#FF5722] transition-colors active:scale-[0.99] text-left"
                        >
                            <div>
                                <p className="font-display font-bold text-xl">{p.credits} crédits</p>
                                <p className="text-xs text-white/60 font-body">{p.label}</p>
                            </div>
                            <div className="text-right">
                                <p className="font-display font-black text-2xl text-[#FFC107]">{p.price}</p>
                                <p className="text-xs text-white/60 font-body">FCFA</p>
                            </div>
                        </button>
                    ))}
                </div>

                {/* Transactions */}
                <h2 className="font-display font-black text-2xl mb-3">Historique</h2>
                {txns.length === 0 ? (
                    <div className="glass rounded-2xl p-6 text-center">
                        <p className="text-white/60 font-body text-sm">Aucune transaction pour le moment</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {txns.map((t) => (
                            <div key={t.id} className="glass rounded-xl p-3 flex items-center justify-between" data-testid={`txn-${t.id}`}>
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-full bg-[#25D366]/20 flex items-center justify-center">
                                        <Check className="w-4 h-4 text-[#25D366]" />
                                    </div>
                                    <div>
                                        <p className="font-body font-semibold text-sm">+{t.credits_added} crédits</p>
                                        <p className="text-[11px] text-white/50 font-body">{t.provider?.toUpperCase()} · {new Date(t.created_at).toLocaleDateString("fr-FR")}</p>
                                    </div>
                                </div>
                                <p className="font-display font-bold text-sm text-[#FFC107]">{t.amount} F</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0f0f0f] border-white/10 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl">Payer avec Mobile Money</DialogTitle>
                    </DialogHeader>
                    {selectedPack && packs[selectedPack] && (
                        <div className="space-y-4">
                            <div className="glass rounded-xl p-3 flex items-center justify-between">
                                <p className="font-body text-sm">{packs[selectedPack].credits} crédits</p>
                                <p className="font-display font-bold text-[#FFC107]">{packs[selectedPack].price} FCFA</p>
                            </div>

                            <div>
                                <Label className="text-white/80 font-body mb-2 block">Opérateur</Label>
                                <div className="grid grid-cols-2 gap-2">
                                    {PROVIDERS.map((p) => (
                                        <button
                                            key={p.id}
                                            data-testid={`provider-${p.id}`}
                                            onClick={() => setProvider(p.id)}
                                            className={`p-3 rounded-xl border font-body text-sm font-semibold ${provider === p.id ? "border-[#FF5722] bg-[#FF5722]/10" : "border-white/10 bg-white/5"}`}
                                        >
                                            {p.name}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="phone" className="text-white/80 font-body">Numéro Mobile Money</Label>
                                <Input
                                    id="phone"
                                    data-testid="mm-phone-input"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="+225 07 00 00 00 00"
                                    className="mt-1 bg-white/5 border-white/10 h-12 text-white placeholder:text-white/30"
                                />
                            </div>

                            <p className="text-[11px] text-white/50 font-body">
                                Paiement simulé pour le MVP. En production, un code USSD/Push sera envoyé sur votre téléphone.
                            </p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            onClick={confirmPay}
                            disabled={paying}
                            data-testid="confirm-payment"
                            className="w-full h-12 orange-btn rounded-full font-body font-bold"
                        >
                            {paying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Traitement...</> : "Payer maintenant"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <BottomNav />
        </div>
    );
}
